import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import LookupAction from "../../components/settings/LookupAction";
import LookupCrud from "../../components/settings/LookupCrud";
import MarketingCampgain from "../../components/settings/MarketingCampgain";
import DiscountEntries from "../../components/settings/DiscountEntries";

const buildSimpleLookup = ({ id, label, controllerUrl, entityLabel }) => ({
  id,
  label,
  component: (
    <LookupAction
      controllerUrl={controllerUrl}
      createLabelButton={`Create New ${entityLabel}`}
      updateLabel={`Edit ${entityLabel}`}
      createLabel={`Create a New ${entityLabel}`}
    />
  ),
});

const buildLookupCrud = ({ id, label, controllerUrl, entityLabel, fields, columns }) => ({
  id,
  label,
  component: (
    <LookupCrud
      controllerUrl={controllerUrl}
      createLabelButton={`Create New ${entityLabel}`}
      updateLabel={`Edit ${entityLabel}`}
      createLabel={`Create a New ${entityLabel}`}
      fields={fields}
      columns={columns}
    />
  ),
});

const SettingsPage = () => {
  const settings = [
    {
      id: "sport_complex",
      label: "Complexo Desportivo",
      children: [
        {
          id: "accessibility",
          label: "Acessos",
          children: [
            {
              id: "entries_discount",
              label: "Desconto de Entradas",
              component: <DiscountEntries />,
            },
          ],
        },
      ],
    },
    {
      id: "marketing",
      label: "Marketing",
      children: [
        buildSimpleLookup({
          id: "marketing_actions",
          label: "Actions",
          controllerUrl: "/marketing-actions",
          entityLabel: "Marketing Action",
        }),
        {
          id: "marketing_campgain",
          label: "Campgain",
          component: (
            <MarketingCampgain
              controllerUrl={"/marketing-campgains"}
              createLabelButton={"Create New Marketing Campgain"}
              updateLabel={"Edit Marketing Campgain"}
              createLabel={"Create a New Marketing Campgain"}
            />
          ),
        },
        buildSimpleLookup({
          id: "marketing_source",
          label: "Marketing Sources",
          controllerUrl: "/marketing-sources",
          entityLabel: "Marketing Source",
        }),
      ],
    },
    {
      id: "classes",
      label: "Classes",
      children: [
        buildSimpleLookup({
          id: "class_modalities",
          label: "Class Modalities",
          controllerUrl: "/class-modalities",
          entityLabel: "Class Modality",
        }),
        buildSimpleLookup({
          id: "class_intensities",
          label: "Class Intensities",
          controllerUrl: "/class-intensities",
          entityLabel: "Class Intensity",
        }),
        buildSimpleLookup({
          id: "class_complexities",
          label: "Class Complexities",
          controllerUrl: "/class-complexities",
          entityLabel: "Class Complexity",
        }),
        buildSimpleLookup({
          id: "class_levels",
          label: "Class Levels",
          controllerUrl: "/class-levels",
          entityLabel: "Class Level",
        }),
        buildSimpleLookup({
          id: "class_physical_conditions",
          label: "Physical Conditions",
          controllerUrl: "/class-physical-conditions",
          entityLabel: "Physical Condition",
        }),
        buildSimpleLookup({
          id: "class_analysis_groups",
          label: "Analysis Groups",
          controllerUrl: "/class-analysis-groups",
          entityLabel: "Analysis Group",
        }),
        buildLookupCrud({
          id: "rooms",
          label: "Rooms",
          controllerUrl: "/rooms",
          entityLabel: "Room",
          fields: [
            { name: "zone_id", label: "Zone ID" },
            { name: "name", label: "Name" },
            { name: "code", label: "Code" },
            { name: "capacity", label: "Capacity", type: "number" },
            {
              name: "status",
              label: "Status",
              options: [
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ],
              defaultValue: "active",
            },
            { name: "notes", label: "Notes" },
          ],
        }),
      ],
    },
    {
      id: "general_lookups",
      label: "General Lookups",
      children: [
        buildSimpleLookup({
          id: "genders",
          label: "Genders",
          controllerUrl: "/genders",
          entityLabel: "Gender",
        }),
        buildSimpleLookup({
          id: "civil_statuses",
          label: "Civil Statuses",
          controllerUrl: "/civil-statuses",
          entityLabel: "Civil Status",
        }),
        buildSimpleLookup({
          id: "document_types",
          label: "Document Types",
          controllerUrl: "/document-types",
          entityLabel: "Document Type",
        }),
        buildSimpleLookup({
          id: "departments",
          label: "Departments",
          controllerUrl: "/departments",
          entityLabel: "Department",
        }),
        buildSimpleLookup({
          id: "roles",
          label: "Roles",
          controllerUrl: "/roles",
          entityLabel: "Role",
        }),
        buildSimpleLookup({
          id: "job_positions",
          label: "Job Positions",
          controllerUrl: "/job-positions",
          entityLabel: "Job Position",
        }),
        buildSimpleLookup({
          id: "titles",
          label: "Titles",
          controllerUrl: "/titles",
          entityLabel: "Title",
        }),
        buildSimpleLookup({
          id: "inventory_types",
          label: "Inventory Types",
          controllerUrl: "/inventory-types",
          entityLabel: "Inventory Type",
        }),
      ],
    },
    {
      id: "financial_lookups",
      label: "Financial & Geo Lookups",
      children: [
        buildLookupCrud({
          id: "payment_providers",
          label: "Payment Providers",
          controllerUrl: "/payment-providers",
          entityLabel: "Payment Provider",
          fields: [
            {
              name: "provider",
              label: "Provider",
              options: [{ value: "stripe", label: "Stripe" }],
              defaultValue: "stripe",
            },
            {
              name: "mode",
              label: "Mode",
              options: [
                { value: "test", label: "Test" },
                { value: "live", label: "Live" },
              ],
              defaultValue: "test",
            },
            { name: "public_key", label: "Public Key" },
            { name: "secret_key", label: "Secret Key", type: "password" },
            {
              name: "webhook_signing_secret",
              label: "Webhook Signing Secret",
              type: "password",
            },
            { name: "is_enabled", label: "Enabled", type: "checkbox", defaultValue: false },
          ],
          columns: [
            { field: "provider", headerName: "Provider", flex: 1, minWidth: 140 },
            { field: "mode", headerName: "Mode", flex: 1, minWidth: 120 },
            {
              field: "is_enabled",
              headerName: "Enabled",
              flex: 1,
              minWidth: 120,
              renderCell: (params) => (params?.row?.is_enabled ? "Yes" : "No"),
            },
            { field: "public_key", headerName: "Public Key", flex: 1.5, minWidth: 220 },
            { field: "secret_key_masked", headerName: "Secret Key", flex: 1.5, minWidth: 220 },
            {
              field: "webhook_signing_secret_masked",
              headerName: "Webhook Secret",
              flex: 1.5,
              minWidth: 220,
            },
          ],
        }),
        buildLookupCrud({
          id: "payment_methods",
          label: "Payment Methods",
          controllerUrl: "/payment-methods",
          entityLabel: "Payment Method",
          fields: [
            { name: "name", label: "Name" },
            { name: "type", label: "Type" },
          ],
        }),
        buildLookupCrud({
          id: "zones",
          label: "Zones",
          controllerUrl: "/zones",
          entityLabel: "Zone",
          fields: [
            { name: "code", label: "Code" },
            { name: "name", label: "Name" },
          ],
        }),
        buildLookupCrud({
          id: "vat_types",
          label: "VAT Types",
          controllerUrl: "/vat-types",
          entityLabel: "VAT Type",
          fields: [
            { name: "code", label: "Code" },
            { name: "rate", label: "Rate", type: "number" },
          ],
        }),
        buildLookupCrud({
          id: "vat_rates",
          label: "VAT Rates",
          controllerUrl: "/vat-rates",
          entityLabel: "VAT Rate",
          fields: [
            { name: "code", label: "Code" },
            { name: "name", label: "Name" },
            { name: "rate", label: "Rate", type: "number" },
            { name: "is_exempt", label: "Is Exempt", type: "checkbox" },
          ],
        }),
        buildLookupCrud({
          id: "countries",
          label: "Countries",
          controllerUrl: "/countries",
          entityLabel: "Country",
          fields: [
            { name: "name", label: "Name" },
            { name: "iso2", label: "ISO2" },
            { name: "iso3", label: "ISO3" },
            { name: "phone_code", label: "Phone Code" },
            { name: "currency_code", label: "Currency Code" },
            { name: "continent", label: "Continent" },
          ],
        }),
      ],
    },
  ];

  const renderNodes = (nodes) =>
    nodes.map((node) => (
      <Accordion
        key={node.id}
        TransitionProps={{ unmountOnExit: true }}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls={`${node.id}-content`}
          id={`${node.id}-header`}
        >
          <Typography component="span">{node.label}</Typography>
        </AccordionSummary>

        <AccordionDetails>
          {node.children && node.children.length > 0
            ? renderNodes(node.children)
            : node.component ?? null}
        </AccordionDetails>
      </Accordion>
    ));

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px", margin: "auto" }}>
      {renderNodes(settings)}
    </Box>
  );
};

export default SettingsPage;
